import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Grid,
  Fade,
  Slide,
  Grow,
  Zoom,
  keyframes,
  Chip,
} from '@mui/material';
import {
  IconUsers,
  IconHeadset,
  IconPercentage,
  IconDots,
  IconPhone,
  IconClock,
  IconTarget,
  IconTrendingUp,
  IconCircle,
  IconAlertCircle,
  IconArrowLeft,
  IconQuestionMark,
} from '@tabler/icons-react';
import { reportsAPI } from '../services/api';
import authService from '../services/authService';

// Animasyon tanımları
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.5); }
  50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.6); }
`;

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  
  const [daily, setDaily] = useState<any>(null);
  const [realtime, setRealtime] = useState<any>(null);
  const [activeAgents, setActiveAgents] = useState<number>(0);
  const [waitingCalls, setWaitingCalls] = useState<number>(0);
  const [hourly, setHourly] = useState<Array<{ hour: string; count: number }>>([]);
  const [queuePerf, setQueuePerf] = useState<Array<any>>([]);
  const [agentsPerf, setAgentsPerf] = useState<Array<any>>([]);

  // Get current user for sidebar
  const currentUser = authService.getCurrentUser();
  const userInitials = currentUser 
    ? `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`.toUpperCase() || currentUser.email?.[0].toUpperCase() || 'U'
    : 'U';
  const userName = currentUser
    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email || 'Kullanıcı'
    : 'Kullanıcı';

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const todayIso = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const startOfDay = `${todayIso}T00:00:00.000Z`;
        const endOfDay = `${todayIso}T23:59:59.999Z`;

        const [d, rt, ag, wc, hd, qp, ap] = await Promise.all([
          reportsAPI.getDailyStats(),
          reportsAPI.getRealtimeStats(),
          reportsAPI.getActiveAgents(),
          reportsAPI.getWaitingCalls(),
          reportsAPI.getHourlyDistribution(todayIso),
          reportsAPI.getQueuePerformance(startOfDay, endOfDay), // ✨ Tarih parametreleri eklendi
          reportsAPI.getAllAgentsPerformance(startOfDay, endOfDay),
        ]);
        
        // ✨ Axios response format: response.data = { success: true, data: {...} }
        // So d.data.data is the actual data object
        setDaily(d?.data?.data || {});
        setRealtime(rt?.data?.data || {});
        
        // Active agents - handle array or object response
        const agentsData = ag?.data?.data || ag?.data || [];
        setActiveAgents(Array.isArray(agentsData) ? agentsData.length : (agentsData?.count || agentsData?.activeAgents || 0));
        
        // Waiting calls - handle array or object response
        const waitingData = wc?.data?.data || wc?.data || [];
        setWaitingCalls(Array.isArray(waitingData) ? waitingData.length : (waitingData?.count || waitingData?.waitingCalls || 0));
        
        // Hourly distribution
        const hourlyData = hd?.data?.data || hd?.data || [];
        const hourlyArray = Array.isArray(hourlyData)
          ? hourlyData
          : Array.isArray(hourlyData?.items)
            ? hourlyData.items
            : (hourlyData?.hours || []);
        setHourly(hourlyArray.map((x: any, idx: number) => ({
          hour: String(x.hour ?? x._id ?? x.h ?? idx).padStart(2, '0') + ':00',
          count: Number(x.totalCalls ?? x.count ?? x.total ?? 0)
        })));
        
        // Queue performance
        const qpData = qp?.data?.data || qp?.data || [];
        const queuePerfArray = Array.isArray(qpData) ? qpData : (qpData?.items || []);
        console.log('📊 Queue performance raw data:', qpData);
        console.log('📊 Queue performance array:', queuePerfArray);
        setQueuePerf(queuePerfArray);
        
        // Agents performance
        const apData = ap?.data?.data || ap?.data || [];
        const agentsPerfArray = Array.isArray(apData) ? apData : (apData?.items || []);
        console.log('📊 Agent performance raw response:', ap);
        console.log('📊 Agent performance data:', apData);
        console.log('📊 Agent performance array:', agentsPerfArray);
        setAgentsPerf(agentsPerfArray);
      } catch (e: any) {
        setError(e?.message || 'Raporlar yüklenemedi');
      } finally {
        setLoading(false);
        setTimeout(() => {
          setIsLoaded(true);
        }, 100);
      }
    };
    void load();
  }, [navigate]);

  if (loading && !daily) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Saatlik dağılım için max değer
  const maxHourlyCount = Math.max(...hourly.map((h: any) => h.count || 0), 1);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        fontFamily: "'Axiforma', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
          zIndex: -1,
        },
      }}
    >
      <Box sx={{ display: "flex" }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: "120px",
            bgcolor: "rgba(30, 58, 138, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
            borderRight: "1px solid rgba(255, 255, 255, 0.1)",
            py: 2,
            px: 1,
            position: "relative",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              fontSize: "1rem", 
              fontWeight: "bold", 
              textAlign: "center",
              mb: 2,
              padding: "8px",
              borderRadius: 2,
              background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
              border: "1px solid rgba(255, 255, 255, 0.1)"
            }}>
              <Zoom in={isLoaded} style={{ transitionDelay: '0.1s' }}>
                <Typography variant="h6" sx={{ color: "white", fontSize: "0.75rem", lineHeight: 1.2 }}>
                  ALOTECH
                </Typography>
              </Zoom>
            </Box>
            
            <Slide direction="right" in={isLoaded} style={{ transitionDelay: '0.3s' }}>
              <Typography variant="caption" sx={{ 
                color: "white", 
                display: "block",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "0.65rem",
                mb: 0.5
              }}>
                Raporlar
              </Typography>
            </Slide>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mb: 4 }}>
              <NavItem icon={<IconUsers size={32} />} label="Dashboard" onClick={() => navigate('/supervisor-dashboard')} />
              <NavItem icon={<IconHeadset size={32} />} label="Calls" onClick={() => navigate('/call-management')} />
              <NavItem icon={<IconPercentage size={32} />} label="Reports" active />
            </Box>

            <Box sx={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: "50%", 
                bgcolor: "#f59e0b", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                boxShadow: "0 4px 15px rgba(245, 158, 11, 0.4)"
              }}>
                {userInitials}
              </Box>
              <Typography variant="caption" sx={{ color: "white", textAlign: "center", fontSize: "0.6rem", fontWeight: 500 }}>
                {userName}
              </Typography>
              <IconDots size={16} color="rgba(255, 255, 255, 0.6)" />
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1, fontSize: "2.5rem" }}>
              Raporlar
            </Typography>
            <Typography variant="h5" sx={{ fontSize: "1.1rem", color: "#64748b" }}>
              Detaylı performans metrikleri ve istatistikler
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Fade in={isLoaded} style={{ transitionDelay: '0.1s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Toplam Çağrı
                      </Typography>
                      <IconTrendingUp size={24} color="#3b82f6" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#1e3a8a" }}>
                      {daily?.totalCalls ?? daily?.total ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Fade in={isLoaded} style={{ transitionDelay: '0.2s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Cevaplanan
                      </Typography>
                      <IconPhone size={24} color="#10b981" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#10b981" }}>
                      {daily?.successfulCalls ?? daily?.answered ?? daily?.answeredCalls ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Fade in={isLoaded} style={{ transitionDelay: '0.3s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Cevapsız
                      </Typography>
                      <IconAlertCircle size={24} color="#ef4444" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#ef4444" }}>
                      {daily?.missed ?? daily?.missedCalls ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Fade in={isLoaded} style={{ transitionDelay: '0.4s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Aktif Temsilci
                      </Typography>
                      <IconUsers size={24} color="#3b82f6" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#1e3a8a" }}>
                      {activeAgents}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          </Grid>

          {/* Real-time Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Grow in={isLoaded} style={{ transitionDelay: '0.5s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Aktif Çağrı
                      </Typography>
                      <IconHeadset size={24} color="#3b82f6" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#1e3a8a" }}>
                      {realtime?.activeCalls ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Grow in={isLoaded} style={{ transitionDelay: '0.6s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Bekleyen Çağrı
                      </Typography>
                      <IconClock size={24} color="#f59e0b" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#f59e0b" }}>
                      {realtime?.waitingCalls ?? waitingCalls}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Grow in={isLoaded} style={{ transitionDelay: '0.7s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Ort. Konuşma (sn)
                      </Typography>
                      <IconTarget size={24} color="#8b5cf6" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#8b5cf6" }}>
                      {realtime?.todayStats?.averageDuration ?? realtime?.averageDuration ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Grow in={isLoaded} style={{ transitionDelay: '0.8s' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", 
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                        Ort. Bekleme (sn)
                      </Typography>
                      <IconClock size={24} color="#ec4899" />
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#ec4899" }}>
                      {realtime?.todayStats?.averageWaitTime ?? realtime?.averageWaitTime ?? 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Box sx={{ mb: 4 }}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)", border: "1px solid rgba(0, 0, 0, 0.05)" }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      onClick={() => setSelectedTab(0)}
                      sx={{
                        px: 3,
                        py: 2,
                        cursor: 'pointer',
                        borderBottom: selectedTab === 0 ? '2px solid #3b82f6' : '2px solid transparent',
                        color: selectedTab === 0 ? '#3b82f6' : '#64748b',
                        fontWeight: selectedTab === 0 ? 600 : 500,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#3b82f6',
                        }
                      }}
                    >
                      Saatlik Dağılım
                    </Box>
                    <Box
                      onClick={() => setSelectedTab(1)}
                      sx={{
                        px: 3,
                        py: 2,
                        cursor: 'pointer',
                        borderBottom: selectedTab === 1 ? '2px solid #3b82f6' : '2px solid transparent',
                        color: selectedTab === 1 ? '#3b82f6' : '#64748b',
                        fontWeight: selectedTab === 1 ? 600 : 500,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#3b82f6',
                        }
                      }}
                    >
                      Kuyruk Performansı
                    </Box>
                    <Box
                      onClick={() => setSelectedTab(2)}
                      sx={{
                        px: 3,
                        py: 2,
                        cursor: 'pointer',
                        borderBottom: selectedTab === 2 ? '2px solid #3b82f6' : '2px solid transparent',
                        color: selectedTab === 2 ? '#3b82f6' : '#64748b',
                        fontWeight: selectedTab === 2 ? 600 : 500,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#3b82f6',
                        }
                      }}
                    >
                      Temsilci Performansı
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ p: 3 }}>
                  {selectedTab === 0 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#374151" }}>
                        Saatlik Dağılım
                      </Typography>
                      {hourly.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Veri yok</Typography>
                      ) : (
                        <Box sx={{ maxHeight: '500px', overflowY: 'auto', pr: 1 }}>
                          {hourly.map((h: any) => (
                            <Box key={h.hour} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ width: 80, mr: 2, flexShrink: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
                                  {h.hour}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, backgroundColor: '#f1f5f9', borderRadius: 9999, border: '1px solid #e2e8f0', height: 24, position: 'relative', overflow: 'hidden' }}>
                                <Box 
                                  sx={{ 
                                    width: `${Math.round((h.count / maxHourlyCount) * 100)}%`, 
                                    height: '100%', 
                                    background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                                    borderRadius: 9999,
                                    transition: 'width 0.5s ease',
                                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                                  }} 
                                />
                              </Box>
                              <Box sx={{ width: 80, ml: 2, textAlign: 'right', flexShrink: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e3a8a" }}>
                                  {h.count}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {selectedTab === 1 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#374151" }}>
                        Kuyruk Performansı
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                              <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Kuyruk</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Toplam</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Cevaplanan</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Cevapsız</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Ort. Bekleme (sn)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {queuePerf.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">Veri yok</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              queuePerf.map((q: any, index: number) => (
                                <TableRow 
                                  key={q.queueId || q._id || q.name || index}
                                  sx={{
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: '#f0f9ff',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                                    },
                                    '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                                    borderBottom: '1px solid #e5e7eb'
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 500, color: "#374151" }}>
                                    {q.name || q.queueName || q.queueId || '-'}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#6b7280" }}>
                                    {q.statistics?.totalCalls ?? q.total ?? q.totalCalls ?? 0}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#10b981" }}>
                                    {q.statistics?.answeredCalls ?? q.answered ?? q.answeredCalls ?? 0}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#ef4444" }}>
                                    {q.statistics?.missedCalls ?? q.missed ?? q.missedCalls ?? 0}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#6b7280" }}>
                                    {q.statistics?.averageWaitTime ?? q.avgWaitTime ?? q.averageWait ?? 0}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {selectedTab === 2 && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3, color: "#374151" }}>
                        Temsilci Performansı
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                              <TableCell sx={{ fontWeight: 700, color: "#374151" }}>Temsilci</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Toplam</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Cevaplanan</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Cevapsız</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>Ort. Konuşma (sn)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {agentsPerf.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">Veri yok</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              agentsPerf.map((a: any, index: number) => (
                                <TableRow 
                                  key={a.agentId || a._id || a.name || index}
                                  sx={{
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: '#f0f9ff',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                                    },
                                    '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                                    borderBottom: '1px solid #e5e7eb'
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 500, color: "#374151" }}>
                                    {a.agentName || a.name || a.agentId || '-'}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#6b7280" }}>
                                    {a.totalCalls ?? a.total ?? 0}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#10b981" }}>
                                    {a.answeredCalls ?? a.answered ?? 0}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#ef4444" }}>
                                    {a.missedCalls ?? a.missed ?? 0}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 500, color: "#6b7280" }}>
                                    {Math.round(a.averageDuration ?? a.averageCallDuration ?? a.avgTalkTime ?? a.averageTalk ?? 0)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// NavItem Component
const NavItem: React.FC<{ icon: React.ReactNode; label: string; active?: boolean; onClick?: () => void }> = ({ icon, label, active, onClick }) => (
  <Box 
    onClick={onClick}
    sx={{ 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "center", 
      gap: 0.5,
      p: 1,
      borderRadius: 2,
      transition: "all 0.3s ease",
      cursor: "pointer",
      bgcolor: active ? "rgba(255, 255, 255, 0.15)" : "transparent",
      "&:hover": {
        bgcolor: "rgba(255, 255, 255, 0.1)",
        transform: "translateY(-2px)",
      }
    }}
  >
    <Box sx={{ color: "white" }}>{icon}</Box>
    <Typography variant="caption" sx={{ color: "white", fontSize: "0.6rem", fontWeight: active ? 700 : 500 }}>
      {label}
    </Typography>
  </Box>
);

export default Reports;
